type StorageOperation = 'getItem' | 'setItem' | 'removeItem';

type StorageFailure = {
  key: string;
  operation: StorageOperation;
  errorName: string;
};

const reportedFailures: Map<string, string> = new Map();

// Telemetry also imports the network store, which needs this adapter during initialization.
export const safeLocalStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: (key: string): string | null => {
    if (typeof window === 'undefined') return null;
    try {
      const value = globalThis.localStorage.getItem(key);
      reportedFailures.delete(`getItem:${key}`);
      return value;
    } catch (error) {
      const failure = storageFailure(key, 'getItem', error);
      if (failure !== null) logger.warn('storage.operation_failed', failure);
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    if (typeof window === 'undefined') return;
    try {
      globalThis.localStorage.setItem(key, value);
      reportedFailures.delete(`setItem:${key}`);
    } catch (error) {
      const failure = storageFailure(key, 'setItem', error);
      if (failure !== null) logger.warn('storage.operation_failed', failure);
    }
  },
  removeItem: (key: string): void => {
    if (typeof window === 'undefined') return;
    try {
      globalThis.localStorage.removeItem(key);
      reportedFailures.delete(`removeItem:${key}`);
    } catch (error) {
      const failure = storageFailure(key, 'removeItem', error);
      if (failure !== null) logger.warn('storage.operation_failed', failure);
    }
  },
};

const logger: { warn(message: string, failure: StorageFailure): void } = {
  warn: (message: string, failure: StorageFailure): void => {
    void import('@/lib/telemetry/logger').then(({ logger }): void => {
      logger.warn(message, failure);
    });
  },
};

function storageFailure(
  key: string,
  operation: StorageOperation,
  // ALLOW-UNKNOWN: browser storage methods and accessors can throw arbitrary values.
  error: unknown,
): StorageFailure | null {
  const errorName =
    error instanceof Error || error instanceof DOMException ? error.name : 'UnknownError';
  const failureKey = `${operation}:${key}`;
  // Repeated chart interactions must not flood telemetry while storage stays unavailable.
  if (reportedFailures.get(failureKey) === errorName) return null;
  reportedFailures.set(failureKey, errorName);
  return { key, operation, errorName };
}
